/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  Navigate, Outlet, Route, Routes, useLocation,
} from 'react-router-dom';
import 'App.css';
import GlobalInjector from 'Store/GlobalInjector';
import DeferredAppStyles from 'Style/DeferredAppStyles';
import {
  FEED_ROUTE,
  FRESHCHAT_TOKEN, FRESHCHAT_URL, ONBOARDING_ROUTE, RESTRICT_ROUTE, USER_EXPIRED_ROUTE,
} from 'Utils/Constants';
import classNames from 'classnames';
import RestrictUserModal from 'Components/common/modal/RestrictUserModal';
import RestrictLayout from 'Components/layout/RestrictLayout';
import MobileAppBanner from 'Components/common/modal/MobileAppBanner';
import { Dropdown } from 'Translation/Dropdown';
import { useSelector } from 'react-redux';
import { selectDisclessPersonality, selectGilroyDiscalimer } from 'Selector/GlobalSearchContentSelector';
import RestrictPage from 'Pages/RestrictPage';
import NotFound from 'Pages/NotFound';
import { Freshchat } from 'reactjs-freshchat';
import Feed from 'Pages/feed/Feed';
import Person from 'Pages/person/Person';
import PeoplePage from 'Pages/people/PeoplePage';
import PeopleGroupDetail from 'Pages/people/PeopleGroupDetail';
import Company from 'Pages/company/Company';
import CompaniesHome from 'Pages/company/CompaniesHome';
import CompanyNotableStaff from 'Pages/company/CompanyNotableStaff';
import DocumentHub from 'Pages/document/DocumentHub';
import IndustriesHome from 'Pages/industries/IndustriesPage';
import IndustriesDetailPage from 'Pages/industries/IndustriesDetailPage';
import ServiceLinesHome from 'Pages/serviceline/serviceLinesPage';
import ServiceLinesDetailPage from 'Pages/serviceline/serviceLinesDetailPage';
import SalesIndex from 'Pages/sri/SalesIndex';
import SearchDetails from 'Pages/search/SearchDetails';
import GlobalSearchRedirections from 'Pages/search/GlobalSearchRedirections';
import AdvanceSearchResults from 'Pages/search/AdvanceSearchResults';
import CustomWidget from 'Pages/customWidget/CustomWidget';
import CompanyComparison from 'Pages/companyComparison/CompanyComparison';
import ClusterDetailPage from 'Pages/company/ClusterDetailPage';
import { prefetchNavRouteModules } from 'Utils/routePrefetch';
import {
  ArcTypes,
  Articles,
  DailyIQCompanies,
  DeepLinkView,
  Favorites,
  GuestSearch,
  Login,
  Onboardingv2,
  SalesforceRouter,
  Settings,
  SimilarPeople,
  SSO,
} from 'Utils/lazyPages';
import Layout from './Components/layout/Layout';
import {
  getLocalStorageValue,
  guestRouteCheck,
  isActive,
  isGuest,
  isSalesforce,
  IsMobile,
  navigateToAuthServer,
  skipOnboarding, isEnterpriseUser,
  getLangCookie,
  isGuestfromEmail,
  Options,
  isSalesforceRoute,
} from './Utils/UtilityFunctions';

const PrivateWrapper = function PrivateWrapper() {
  const { pathname, search }: { pathname: string, search: string } = useLocation();
  if (isActive() || isGuestfromEmail()) {
    if (isGuest() || isGuestfromEmail()) {
      if (!guestRouteCheck() && !isGuestfromEmail()) {
        navigateToAuthServer(pathname + search);
      } else {
        return <Layout><Outlet /></Layout>;
      }
    }
    if (pathname !== ONBOARDING_ROUTE && !skipOnboarding()) return <Navigate to="/onboarding" />;
    const restrictFlag = localStorage.getItem('restrictFlag') === 'true';
    if (pathname.includes('restrict')) {
      return restrictFlag ? <Outlet /> : <Navigate to={FEED_ROUTE} />;
    }
    if (restrictFlag) {
      return <Navigate to={RESTRICT_ROUTE} />;
    }
    return <Layout><Outlet /></Layout>;
  }
  navigateToAuthServer(pathname + search);
  return null;
};

const getApp = function getApp(app: string, target: string) {
  const root = document.querySelector(target);
  const node = root?.classList?.add(app);
  return node;
};

type Props = {
  children: ReactNode,
};

const App = function App(props: Props) {
  const { children } = props;
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState<boolean>(false);
  const { pathname }: { pathname: string } = useLocation();
  const { showDisclaimer } = useSelector(selectGilroyDiscalimer);
  GlobalInjector();
  const disclessPersonality = useSelector(selectDisclessPersonality);
  const isAuthRoute = /\/password|\/webapp\/|^\/sso\//.test(pathname);
  const showAuthStyle = isAuthRoute;
  const showTranslatorStyle = (isActive() || isGuest() || isGuestfromEmail() || shouldRenderDropdown) && !isAuthRoute;
  const showFreshBotStyle = isActive();

  useEffect(() => {
    window.onbeforeunload = () => {
      window.scrollTo(0, 0);
    };
  }, []);

  useEffect(() => {
    const schedule = window.requestIdleCallback ?? ((cb: IdleRequestCallback) => window.setTimeout(cb, 1500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const idleId = schedule(() => prefetchNavRouteModules());
    return () => cancel(idleId as number);
  }, []);

  useEffect(() => (isSalesforce() || isSalesforceRoute() ? getApp('salesForceApp', '#root') : getApp('salesApp', '#root')), []);
  useEffect(() => (getApp(!isEnterpriseUser() ? 'freeUser' : 'notFreeUser', 'body')), []);
  useEffect(() => {
    document.documentElement.lang = getLangCookie();
  }, [getLangCookie()]);

  useEffect(() => {
    const node = document.querySelector('body');
    if (pathname === ONBOARDING_ROUTE) {
      node?.classList.add('onboarding');
      setShouldRenderDropdown(true);
    } else if (isGuestfromEmail()) {
      setShouldRenderDropdown(true);
      node?.classList.remove('onboarding');
    } else {
      setShouldRenderDropdown(false);
      node?.classList.remove('onboarding');
    }
  }, [pathname]);

  return (
    <div className={classNames('App', { nutenixUser: disclessPersonality })}>
      <DeferredAppStyles
        showAuthStyle={showAuthStyle}
        showTranslatorStyle={showTranslatorStyle}
        showFreshBotStyle={showFreshBotStyle}
      />
      {!shouldRenderDropdown && isActive() && <Dropdown options={Options} />}
      {isActive() ? (
        <Freshchat
          token={FRESHCHAT_TOKEN}
          host={FRESHCHAT_URL}
          externalId={getLocalStorageValue('email') || ''}
          email={getLocalStorageValue('email') || ''}
          firstName={getLocalStorageValue('fullname') || ''}
          config={{
            headerProperty: {
              hideChatButton: true,
            },
            content: {
              headers: {
                faq: 'xiQ University',
              },
            },
          }}
          faqTags={{
            tags: ['Sales X'],
            filterType: 'category',
          }}
        />
      ) : null}
      {(IsMobile() && !isSalesforce() && !showDisclaimer) ? (
        <MobileAppBanner />
      ) : null}
      <Routes>
        <Route path="sso/:token" element={<SSO />} />
        <Route path="/webapp/" element={<DeepLinkView />} />
        <Route path="/password*" element={<Login />} />
        <Route
          path="article"
          element={(
            <Layout><Articles /></Layout>
          )}
        />
        <Route
          path="guestSearch"
          element={(
            <Layout><GuestSearch /></Layout>)}
        />
        <Route
          path={USER_EXPIRED_ROUTE}
          element={(
            <RestrictLayout>
              <RestrictUserModal />
            </RestrictLayout>
          )}
        />
        <Route element={<PrivateWrapper />}>
          <Route path="onboarding" element={<Onboardingv2 />} />
          <Route path="feed/:section" element={<Feed />} />
          <Route path="feed" element={<Feed />} />
          <Route path="companies" element={<CompaniesHome />} />
          <Route path="company/:section" element={<Company />} />
          <Route path="notablestaff" element={<CompanyNotableStaff />} />
          <Route path="person/:section" element={<Person />} />
          <Route path="settings" element={<Settings />} />
          <Route path="favorites" element={<Favorites />} />
          <Route path="people" element={<PeoplePage />} />
          <Route path="comparecompany" element={<CompanyComparison />} />
          <Route path="industries" element={<IndustriesHome />} />
          <Route path="documents" element={<DocumentHub />} />
          <Route path="industry/:section" element={<IndustriesDetailPage />} />
          <Route path="servicelines" element={<ServiceLinesHome />} />
          <Route path="serviceline/:section" element={<ServiceLinesDetailPage />} />
          <Route path="search" element={<GlobalSearchRedirections />} />
          <Route path="search/globalsearch" element={<SearchDetails />} />
          <Route path="search/advanced" element={<AdvanceSearchResults />} />
          <Route path="groupdetails/:section" element={<PeopleGroupDetail />} />
          <Route path="arctypes/:section" element={<ArcTypes />} />
          <Route path="sri/:section" element={<SalesIndex />} />
          <Route path="news" element={<CustomWidget />} />
          <Route path="similarpeople" element={<SimilarPeople />} />
          <Route path="salesforce/:section" element={<SalesforceRouter />} />
          {children}
          <Route path="notfound" element={<NotFound />} />
          <Route path="restrict" element={<RestrictPage />} />
          <Route path="dailyiq" element={<DailyIQCompanies />} />
          <Route path="clusterdetails/:section" element={<ClusterDetailPage />} />
        </Route>
      </Routes>
    </div>
  );
};


export default App;
